/**
 * SmokeRunContext — In-App Smoke Tests run state, app-wide so a run survives
 * navigation (the user walks the real app while the HUD floats on top). Mirrors
 * the TwilioContext/global-overlay pattern: a provider mounted inside the Router
 * that renders the floating SmokeRunHUD and exposes start/advance/result/export.
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { smokeTestApi, type SmokeDefinition, type SmokeItem, type ItemStatus } from '../services/smokeTestApi';
import { debugService } from '../services/debugService';
import { todosApi } from '../services/todosApi';
import { useToast } from './ToastContext';

// Smoke runs are usually driven right after a deploy — exactly when the backend
// is most likely to throw a transient 502 ("upstream error") during a container
// swap. Retry the persist/finalize calls on network/5xx errors so a brief blip
// doesn't drop a mark or strand the run. 4xx (e.g. a bad status) never retries.
async function withRetry<T>(fn: () => Promise<T>, retries = 3, delayMs = 1500): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      const status = (e as { response?: { status?: number } })?.response?.status;
      const transient = !status || status >= 500; // network error or 5xx (incl. 502)
      if (!transient || attempt === retries) throw e;
      await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
    }
  }
  throw lastErr;
}

export interface FlatItem { section_id: string; section_title: string; item: SmokeItem; }
// status is optional: a note can be saved before a Pass/Fail/Skip is chosen.
export interface ItemDraft { status?: ItemStatus; note?: string; linked_ticket_id?: number | null }

interface SmokeRunValue {
  active: boolean;
  token: string | null;
  definition: SmokeDefinition | null;
  flat: FlatItem[];
  index: number;
  results: Record<string, ItemDraft>;
  minimized: boolean;
  busy: boolean;
  current: FlatItem | null;
  finishedToken: string | null;
  startRun: (slug: string) => Promise<void>;
  setResult: (status: ItemStatus, note?: string) => Promise<void>;
  saveNote: (note: string) => Promise<void>;
  goToCurrent: () => void;
  next: () => void;
  prev: () => void;
  snapshot: () => Promise<void>;
  reportBug: (note?: string) => Promise<void>;
  finish: () => Promise<string | null>;
  cancel: () => void;
  setMinimized: (b: boolean) => void;
}

const SmokeRunContext = createContext<SmokeRunValue | null>(null);
export const useSmokeRun = (): SmokeRunValue => {
  const ctx = useContext(SmokeRunContext);
  if (!ctx) throw new Error('useSmokeRun must be used within SmokeRunProvider');
  return ctx;
};

// These routes render outside AuthenticatedShell, where this provider lives (`App.tsx`), so
// moving there would unmount the run and its marks with it. Next and Prev stay on the page
// and "Go →" opens a new tab instead: 55 checklist items target the client portal alone.
const OUTSIDE_SHELL = /^\/(portal|book|booking|a|f|d|demo|csat|csat-thanks|newsletter|unsubscribe)(\/|$)/;
export const outsideShell = (route: string | null | undefined): boolean => !!route && OUTSIDE_SHELL.test(route);

function flatten(def: SmokeDefinition): FlatItem[] {
  return def.sections.flatMap((s) => s.items.map((item) => ({ section_id: s.id, section_title: s.title, item })));
}

export const SmokeRunProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [active, setActive] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [definition, setDefinition] = useState<SmokeDefinition | null>(null);
  const [flat, setFlat] = useState<FlatItem[]>([]);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<string, ItemDraft>>({});
  const [minimized, setMinimized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [finishedToken, setFinishedToken] = useState<string | null>(null);
  // Warn at most once per run that a save couldn't reach the server (don't spam
  // a toast per failed item during a 502 window). Reset on each new run.
  const saveWarnedRef = useRef(false);
  const warnSaveFailed = useCallback(() => {
    if (saveWarnedRef.current) return;
    saveWarnedRef.current = true;
    toast.warning("Couldn't reach the server to save that — your marks are kept here and will re-sync when you click Finish. (The server may be briefly restarting after a deploy.)");
  }, [toast]);

  const current = flat[index] || null;

  const goTo = useCallback((items: FlatItem[], i: number, runToken: string | null) => {
    const it = items[i];
    if (!it) return;
    if (runToken) debugService.setSmokeItem(it.item.id);
    if (it.item.target_route && !outsideShell(it.item.target_route)) navigate(it.item.target_route);
  }, [navigate]);

  const startRun = useCallback(async (slug: string) => {
    setBusy(true);
    try {
      const { run_token, definition: def } = await smokeTestApi.startRun(slug, window.location.hostname.includes('staging') ? 'staging' : 'production');
      const items = flatten(def);
      if (items.length === 0) { console.warn(`[smoke] checklist "${slug}" has no items — not starting`); return; }
      debugService.startSmokeSession(run_token);
      saveWarnedRef.current = false;
      setToken(run_token); setDefinition(def); setFlat(items); setIndex(0);
      setResults({}); setMinimized(false); setFinishedToken(null); setActive(true);
      goTo(items, 0, run_token);
    } catch (e) { console.error('[smoke] startRun failed', e); }
    finally { setBusy(false); }
  }, [goTo]);

  const setResult = useCallback(async (status: ItemStatus, note?: string) => {
    if (!token || !current) return;
    const existing = results[current.item.id];
    const finalNote = note ?? existing?.note;
    const draft: ItemDraft = { status, note: finalNote, linked_ticket_id: existing?.linked_ticket_id ?? null };
    setResults((r) => ({ ...r, [current.item.id]: draft }));
    try {
      await withRetry(() => smokeTestApi.saveItemResult(token, { section_id: current.section_id, item_id: current.item.id, status, note: finalNote, linked_ticket_id: draft.linked_ticket_id }));
    } catch (e) { console.error('[smoke] saveItemResult failed', e); warnSaveFailed(); }
  }, [token, current, results, warnSaveFailed]);

  // Persist a note even before a Pass/Fail is chosen (note-only records as
  // 'skip' so it survives navigation and lands in the export).
  const saveNote = useCallback(async (note: string) => {
    if (!token || !current) return;
    const existing = results[current.item.id];
    if (!note && !existing) return; // nothing typed, nothing to persist
    const status: ItemStatus = existing?.status || 'skip';
    setResults((r) => ({ ...r, [current.item.id]: { ...existing, status, note } }));
    try {
      await withRetry(() => smokeTestApi.saveItemResult(token, { section_id: current.section_id, item_id: current.item.id, status, note, linked_ticket_id: existing?.linked_ticket_id ?? null }));
    } catch (e) { console.error('[smoke] saveNote failed', e); warnSaveFailed(); }
  }, [token, current, results, warnSaveFailed]);

  const goToCurrent = useCallback(() => {
    const route = flat[index]?.item.target_route;
    if (outsideShell(route)) { window.open(route as string, '_blank', 'noopener'); return; }
    goTo(flat, index, token);
  }, [goTo, flat, index, token]);

  // Attach the just-left item's dev-mode capture slice before moving on.
  const attachCurrentCapture = useCallback(async () => {
    if (!token || !current) return;
    try {
      const capture = debugService.exportSmokeSession(token, current.item.id);
      if (capture.eventCount > 0) await smokeTestApi.attachCapture(token, { item_id: current.item.id, capture_json: capture });
    } catch (e) { /* non-fatal */ }
  }, [token, current]);

  // Pure index update + side effects OUTSIDE the updater (React double-invokes
  // updaters in StrictMode, which would double-navigate/double-tag).
  const next = useCallback(() => {
    void attachCurrentCapture();
    const ni = Math.min(index + 1, flat.length - 1);
    setIndex(ni);
    goTo(flat, ni, token);
  }, [attachCurrentCapture, index, flat, token, goTo]);

  const prev = useCallback(() => {
    void attachCurrentCapture(); // symmetric with next — don't drop the left item's evidence
    const ni = Math.max(index - 1, 0);
    setIndex(ni);
    goTo(flat, ni, token);
  }, [attachCurrentCapture, index, flat, token, goTo]);

  const snapshot = useCallback(async () => {
    if (!current) return;
    await debugService.captureScreenshot(`${current.section_id}/${current.item.id}`);
  }, [current]);

  const reportBug = useCallback(async (noteArg?: string) => {
    if (!token || !current || !definition) return;
    setBusy(true);
    try {
      const cap = debugService.exportSmokeSession(token, current.item.id);
      const note = noteArg ?? results[current.item.id]?.note;
      const desc = [
        `Smoke test: ${definition.title} (\`${definition.slug}\`)`,
        `Item: ${current.section_id} / ${current.item.id} — ${current.item.title}`,
        current.item.expected ? `Expected: ${current.item.expected}` : '',
        note ? `Tester note: ${note}` : '',
        `Dev-mode capture: ${cap.errorCount} error(s), ${cap.apiSummary.errors} API error(s) of ${cap.apiSummary.total} call(s).`,
      ].filter(Boolean).join('\n');
      const created = await todosApi.create({ title: `Smoke bug: ${current.item.title}`.slice(0, 120), description: desc, task_type: 'todo', category: 'internal', priority: 'high' } as any);
      const ticketId = (created as any)?.data?.id ?? null;
      // Stamp the link on the result (keep the existing status, default fail; keep the live note).
      const existing = results[current.item.id];
      const st = existing?.status || 'fail';
      setResults((r) => ({ ...r, [current.item.id]: { status: st, note, linked_ticket_id: ticketId } }));
      await smokeTestApi.saveItemResult(token, { section_id: current.section_id, item_id: current.item.id, status: st, note, linked_ticket_id: ticketId });
    } catch (e) { console.error('[smoke] reportBug failed', e); }
    finally { setBusy(false); }
  }, [token, current, definition, results]);

  const finish = useCallback(async (): Promise<string | null> => {
    if (!token) return null;
    setBusy(true);
    const t = token;
    try {
      // Re-sync every locally-held mark before finalizing: a transient 502 mid-
      // run can have dropped individual saves, so re-persist them all now (with
      // retry) — the server's summary reads the persisted rows, so this is what
      // makes the export complete even after a blip.
      for (const fi of flat) {
        const draft = results[fi.item.id];
        if (!draft?.status) continue;
        await withRetry(() => smokeTestApi.saveItemResult(t, { section_id: fi.section_id, item_id: fi.item.id, status: draft.status!, note: draft.note, linked_ticket_id: draft.linked_ticket_id ?? null }));
      }
      // Per-item captures are attached on each advance; the run's evidence is
      // the sum of those. (No separate full-run blob — exportRun reads per-item
      // captures only, and a whole-run dump of every screenshot can blow the
      // request body limit.)
      await attachCurrentCapture();
      await withRetry(() => smokeTestApi.finalizeRun(t));
    } catch (e) {
      // Finalize couldn't reach the server. Do NOT tear the run down — keep it
      // active so the user can retry Finish without losing a single mark, and
      // tell them plainly (the old code silently navigated to a broken export,
      // which is the "upstream error" page).
      console.error('[smoke] finish failed', e);
      setBusy(false);
      saveWarnedRef.current = true; // we've now told them; don't also fire the per-save warn
      toast.error("Couldn't reach the server to finish the run — it may be briefly restarting (common right after a deploy). Your marks are all kept; click Finish & export again in a moment.");
      return null;
    }
    // Success only past here: stop capture, mark finished, tear down.
    debugService.stopSmokeSession();
    setActive(false); setFinishedToken(t); setToken(null); setDefinition(null); setFlat([]); setIndex(0);
    setBusy(false);
    try { toast.success('Run complete — saved with its dev capture (dev mode restored)'); } catch { /* non-fatal */ }
    return t;
  }, [token, flat, results, attachCurrentCapture, toast]);

  const cancel = useCallback(() => {
    debugService.stopSmokeSession();
    setActive(false); setToken(null); setDefinition(null); setFlat([]); setIndex(0); setResults({});
  }, []);

  const value = useMemo<SmokeRunValue>(() => ({
    active, token, definition, flat, index, results, minimized, busy, current, finishedToken,
    startRun, setResult, saveNote, goToCurrent, next, prev, snapshot, reportBug, finish, cancel, setMinimized,
  }), [active, token, definition, flat, index, results, minimized, busy, current, finishedToken, startRun, setResult, saveNote, goToCurrent, next, prev, snapshot, reportBug, finish, cancel]);

  return (
    <SmokeRunContext.Provider value={value}>
      {children}
      <SmokeRunHUDLazy />
    </SmokeRunContext.Provider>
  );
};

// Lazy import to avoid a circular import (HUD imports useSmokeRun from here).
const SmokeRunHUD = React.lazy(() => import('../components/smoke/SmokeRunHUD'));
const SmokeRunHUDLazy: React.FC = () => (
  <React.Suspense fallback={null}><SmokeRunHUD /></React.Suspense>
);

export default SmokeRunProvider;
